import React from "react";
import { useLocation } from "react-router-dom";
// reactstrap components
import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container
} from "reactstrap";

const navItems = [
  { label: "About", href: "/about" },
  { label: "Dogs", href: "/dogs" },
  { label: "Litters", href: "/litters" },
  { label: "Contact", href: "/contact" }
];

function MainNavbar({ transparent = true }) {
  const [collapseOpen, setCollapseOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const menuButtonRef = React.useRef(null);
  const { pathname } = useLocation();

  const closeMobileMenu = React.useCallback((returnFocus = false) => {
    document.documentElement.classList.remove("nav-open");
    document.body.classList.remove("mobile-nav-open");
    setCollapseOpen(false);

    if (returnFocus) {
      window.setTimeout(() => {
        menuButtonRef.current?.focus();
      }, 0);
    }
  }, []);

  React.useEffect(() => {
    const updateNavbar = () => {
      setScrolled(window.scrollY > 20);
    };

    updateNavbar();
    window.addEventListener("scroll", updateNavbar, { passive: true });

    return () => window.removeEventListener("scroll", updateNavbar);
  }, []);

  React.useEffect(() => {
    closeMobileMenu();
  }, [pathname, closeMobileMenu]);

  React.useEffect(() => {
    document.body.classList.toggle("mobile-nav-open", collapseOpen);

    return () => document.body.classList.remove("mobile-nav-open");
  }, [collapseOpen]);

  React.useEffect(() => {
    if (!collapseOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMobileMenu(true);
        return;
      }

      if (event.key !== "Tab") return;

      const mobileMenu = document.getElementById("site-mobile-menu");
      const focusableElements = [
        menuButtonRef.current,
        ...(mobileMenu
          ? Array.from(mobileMenu.querySelectorAll("a[href], button:not([disabled])"))
          : [])
      ].filter(Boolean);

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeMobileMenu, collapseOpen]);

  const toggleMobileMenu = () => {
    setCollapseOpen(currentOpen => {
      const nextOpen = !currentOpen;

      document.documentElement.classList.toggle("nav-open", nextOpen);
      document.body.classList.toggle("mobile-nav-open", nextOpen);

      return nextOpen;
    });
  };

  const isActive = href => pathname === href || pathname.startsWith(`${href}/`);
  const transparentAtTop = transparent && !scrolled && !collapseOpen;

  return (
    <>
      {collapseOpen ? (
        <div
          id="bodyClick"
          onClick={() => closeMobileMenu(true)}
        />
      ) : null}
      <Navbar
        className={`fixed-top site-navbar${
          transparentAtTop ? " site-navbar-transparent" : " site-navbar-solid"
        }`}
        expand="lg"
      >
        <Container>
          <div className="navbar-translate">
            <NavbarBrand
              href="/"
              id="navbar-brand"
            >
              <img
                  alt="Endgame Sport Dogs home"
                  className="navLogo"
                  src={require("assets/img/logo-v1.png")}
              ></img>
            </NavbarBrand>
            <button
              ref={menuButtonRef}
              className="navbar-toggler navbar-toggler"
              onClick={toggleMobileMenu}
              aria-expanded={collapseOpen}
              aria-controls="site-mobile-menu"
              aria-label={collapseOpen ? "Close navigation menu" : "Open navigation menu"}
              type="button"
            >
              <span className="mobile-menu-icon mobile-menu-icon-hamburger" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </span>
              <span className="mobile-menu-icon mobile-menu-icon-close" aria-hidden="true">
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
          <Collapse
            className="justify-content-end"
            id="site-mobile-menu"
            isOpen={collapseOpen}
            navbar
          >
            <Nav navbar>
              {navItems.map(item => {
                const active = isActive(item.href);

                return (
                  <NavItem key={item.href}>
                    <NavLink
                      aria-current={active ? "page" : undefined}
                      className={active ? "active" : undefined}
                      href={item.href}
                      onClick={() => closeMobileMenu()}
                    >
                      {item.label}
                    </NavLink>
                  </NavItem>
                );
              })}
            </Nav>
            <div className="mobile-menu-footer">
              <a
                className="mobile-menu-facebook"
                href="https://www.facebook.com/endgamesportdogs"
                aria-label="Endgame Sport Dogs on Facebook"
              >
                <img
                  alt=""
                  src={require("assets/img/facebook3.png")}
                />
              </a>
              <p>
                © {new Date().getFullYear()},{" "}
                <a href="mailto:adriana@endgamedogs.com">
                  Endgame Sport Dogs, Adriana Nottestad
                </a>.
              </p>
            </div>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
}

export default MainNavbar;
